import styled from '@emotion/styled';
import { Modal, ModalProps } from 'antd';
import PayWithSubWindow from 'bizComponents/pay/PayWithSubWindow';
import CompatQrPay from 'bizComponents/pay/CompatQrPay';
import Price from 'components/Price';
import useTrackingRef from 'hooks/useTrackingRef';
import { isNil } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import nimInst from 'services/nim';
import { simpleStringify } from 'components/RouterQuery';
import { useAuthorization } from 'components/Authorization';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  > * {
    margin-bottom: 17px;
    text-align: center;
  }
`;

export interface CashierModalMetaProps {
  currency: General.Currency;
  price: number;
  config: Pay.PayConfig;
}
export interface CashierModalProps extends ModalProps {
  //   expired: boolean;
  onRefresh: () => Promise<any>;
  onPayed: () => void;
  meta?: CashierModalMetaProps;
  content?: React.ReactNode;
}

const CashierModal = (props: CashierModalProps) => {
  const { meta, onRefresh, onPayed, content = null, ...otherProps } = props;
  const { token } = useAuthorization();
  const [expired, setExpired] = useState(false);
  const [payed, setPayed] = useState(false);
  const refs = useTrackingRef({ onRefresh, onPayed });
  const payComp = useMemo(() => {
    if (!meta) {
      return null;
    }
    const { currency, config } = meta;
    if (config.payType === 'alipay' || config.payType === 'wxpay') {
      const { qrCode } = config;
      return (
        <CompatQrPay
          payType={config.payType}
          qrCode={qrCode}
          expired={expired}
          onRefresh={onRefresh}
        />
      );
    }

    if (payed) {
      return null;
    }

    const {
      location: { origin, pathname },
    } = window;

    if (config.payType === 'airwallex') {
      const { paymentId, clientSecret } = config;

      return (
        <PayWithSubWindow
          src={`${origin}${pathname}#/airwallex?${simpleStringify({
            intentId: paymentId,
            clientSecret,
            currency,
            token,
          })}`}
          expired={expired}
          name="AIRWALLEX_PAY_WINDOW"
          onRefresh={onRefresh}
        />
      );
    }

    if (config.payType === 'pingpong') {
      return (
        <PayWithSubWindow
          src={config.paymentUrl}
          expired={expired}
          name="PINGPONG_PAY_WINDOW"
          onRefresh={onRefresh}
        />
      );
    }

    if (config.payType === 'lianlian') {
      return (
        <PayWithSubWindow
          src={config.paymentUrl}
          expired={expired}
          name="LIANLIAN_PAY_WINDOW"
          onRefresh={onRefresh}
        />
      );
    }

    return null;
  }, [meta, payed, onRefresh, expired, token]);
  useEffect(() => {
    setExpired(false);

    return nimInst.on('sys:message:pay', (data) => {
      const {
        content: { result, orderCode },
      } = data;

      if (!isNil(orderCode) && orderCode !== meta?.config?.orderCode) {
        return;
      }

      if (result === 'success') {
        setPayed(true);

        const {
          current: { onPayed },
        } = refs;

        onPayed();
      }

      if (result === 'cancel' || result === 'fail') {
        setExpired(true);
      }
    });
  }, [meta?.config?.orderCode, refs]);
  return (
    <Modal footer={null} maskClosable={false} {...otherProps}>
      <Container>
        {content}
        {payComp}
        <Price value={meta?.price || 0} format="0,0.00" currency={meta?.currency} />
      </Container>
    </Modal>
  );
};
export default CashierModal;
